import { ReviewSortEnum } from '@iheartjane/dm-sdk';
import {
  Button,
  ChevronDownIcon,
  List,
  Loading,
  Popover,
  Typography,
} from '@iheartjane/reefer';
import { useInView } from 'react-intersection-observer';

import { useProductReviewsFetcher } from '@jane/bloom/hooks/useProductReviewsFetcher';
import type {
  ProductReviewViewModel,
  ProductReviewsPaginationViewModel,
  ProductReviewsSortOptions,
} from '@jane/bloom/viewModels/productReviews';

import { ProductReview } from './productReview';

import styles from './productReviews.module.css';

type ProductReviewsProps = {
  initialPagination: ProductReviewsPaginationViewModel;
  initialReviews: ProductReviewViewModel[];
  productId: number;
  productSlug: string | undefined;
  // Count of reviews with comments — the user-facing count for this list.
  reviewCount: number;
};

const PRODUCT_REVIEWS_SORT_LABELS: Record<
  ReviewSortEnum,
  ProductReviewsSortOptions['label']
> = {
  [ReviewSortEnum.Rating]: 'Rating',
  [ReviewSortEnum.Recent]: 'Recent',
  [ReviewSortEnum.Upvote]: 'Helpful',
};

const productReviewsSortOptions: ProductReviewsSortOptions[] = Object.entries(
  PRODUCT_REVIEWS_SORT_LABELS
).map(([value, label]) => ({ label, value: value as ReviewSortEnum }));

export function ProductReviews({
  initialPagination,
  initialReviews,
  productId,
  productSlug,
  reviewCount,
}: ProductReviewsProps) {
  const { ref: sentinelRef } = useInView({
    onChange: (inView) => {
      if (inView && canLoadMoreReviews) {
        handleLoadMore();
      }
    },
  });
  const {
    canLoadMoreReviews,
    currentSortOrder,
    displayableReviews,
    handleLoadMore,
    handleSort,
    isLoading,
    upvote,
  } = useProductReviewsFetcher({
    initialPagination,
    initialReviews,
    productId,
    productSlug,
  });

  return (
    <div className={styles['pdpReviewsWrapper']} data-testid="pdp-reviews">
      {initialPagination.totalCount ? (
        <div className={styles['pdpReviewsSortSelectorContainer']}>
          <div className={styles['pdpReviewsCount']}>
            <Typography variant="body-bold" color="text-main">
              {`1-${displayableReviews.length} of ${reviewCount} reviews`}
            </Typography>
          </div>

          <Popover
            label=""
            target={
              <Button
                label={`Sort: ${PRODUCT_REVIEWS_SORT_LABELS[currentSortOrder]}`}
                variant="tertiary"
                endIcon={<ChevronDownIcon color="text-main" size="sm" />}
              />
            }
          >
            {({ closePopover }) => (
              <>
                {productReviewsSortOptions.map((option) => (
                  <List.Item
                    ariaLabel={option.label}
                    ml={8}
                    mr={8}
                    key={option.value}
                    onClick={() => {
                      handleSort(option.value);
                      closePopover();
                    }}
                  >
                    <Typography>{option.label}</Typography>
                  </List.Item>
                ))}
              </>
            )}
          </Popover>
        </div>
      ) : null}
      <hr className={styles['pdpReviewsActivitiesDividerMobile']} />

      {displayableReviews.map((review) => (
        <ProductReview
          key={review.id}
          productId={productId}
          productSlug={productSlug}
          review={review}
          upvote={upvote}
        />
      ))}

      {isLoading && (
        <div className={styles['loading']}>
          <Loading color="black" />
        </div>
      )}

      <div ref={sentinelRef} aria-hidden="true" />
    </div>
  );
}
